import { parseTMJ } from '../core/TMJParser.js';
import { exportTMJ, exportTMJBlob } from '../core/TMJExporter.js';
import { exportTMX } from '../core/TMXExporter.js';
import { RecentFiles } from './RecentFiles.js';
import { validateTMJ } from './TMJValidator.js';

/**
 * Load/save TMJ files via File System Access API (with download fallback).
 */
export class FileManager {
  constructor(state, mapCanvas) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this._fileHandle = null;
    this._onSaveCallbacks = [];
    this.recentFiles = new RecentFiles();
  }

  /** Register a callback that fires after a successful save. */
  onSave(fn) {
    this._onSaveCallbacks.push(fn);
    return () => { this._onSaveCallbacks = this._onSaveCallbacks.filter(f => f !== fn); };
  }

  _notifySaved() {
    // Track in recent files on save
    const fileName = this.state.get('fileName');
    if (fileName) {
      this.recentFiles.add(fileName, {
        width: this.state.get('mapWidth'),
        height: this.state.get('mapHeight'),
        layerCount: (this.state.get('layers') || []).length,
        tilesets: this.state.get('tilesets') || [],
      });
    }
    for (const fn of this._onSaveCallbacks) {
      try { fn(); } catch (e) { console.error('Save callback error:', e); }
    }
  }

  /** Open a TMJ file and load it into editor state. */
  async openFile() {
    try {
      // Try File System Access API first
      if ('showOpenFilePicker' in window) {
        const [handle] = await window.showOpenFilePicker({
          types: [{
            description: 'Tiled Map JSON',
            accept: { 'application/json': ['.tmj', '.json'] },
          }],
        });
        this._fileHandle = handle;
        const file = await handle.getFile();
        const text = await file.text();
        const result = validateTMJ(text, file.name);
        if (!result.ok) { alert(result.error); return; }
        await this._loadTMJ(result.tmj, file.name);
      } else {
        // Fallback: file input
        await this._openViaInput();
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // User cancelled
      console.error('Failed to open file:', err);
      alert(`Failed to open file: ${err.message}`);
    }
  }

  /** Save the current map as TMJ. */
  async saveFile() {
    try {
      if (this._fileHandle) {
        // Write back to same file
        const blob = exportTMJBlob(this.state);
        const writable = await this._fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        this.state.set('dirty', false);
        this._notifySaved();
        return;
      }

      await this.saveFileAs();
    } catch (err) {
      console.error('Failed to save file:', err);
      alert(`Failed to save: ${err.message}`);
    }
  }

  /** Save as — always prompts for location. */
  async saveFileAs() {
    const blob = exportTMJBlob(this.state);
    const fileName = this.state.get('fileName') || 'untitled.tmj';

    try {
      if ('showSaveFilePicker' in window) {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: 'Tiled Map JSON',
            accept: { 'application/json': ['.tmj', '.json'] },
          }],
        });
        this._fileHandle = handle;
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        this.state.set('dirty', false);
        this.state.set('fileName', handle.name);
        this._notifySaved();
      } else {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        this.state.set('dirty', false);
        this._notifySaved();
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      throw err;
    }
  }

  /** Export the current map as TMX (Tiled XML). */
  async exportAsTMX() {
    const xml = exportTMX(this.state);
    const fileName = (this.state.get('fileName') || 'untitled.tmj').replace(/\.(tmj|json)$/i, '.tmx');

    try {
      if ('showSaveFilePicker' in window) {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [{
            description: 'Tiled Map XML',
            accept: { 'application/xml': ['.tmx'] },
          }],
        });
        const writable = await handle.createWritable();
        await writable.write(xml);
        await writable.close();
      } else {
        // Fallback: download
        const blob = new Blob([xml], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to export TMX:', err);
      alert(`Failed to export TMX: ${err.message}`);
    }
  }

  async _openViaInput() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.tmj,.json';
      input.addEventListener('change', async () => {
        try {
          const file = input.files[0];
          if (!file) { resolve(); return; }
          const text = await file.text();
          const result = validateTMJ(text, file.name);
          if (!result.ok) { alert(result.error); resolve(); return; }
          await this._loadTMJ(result.tmj, file.name);
        } catch (err) {
          console.error('Failed to open file:', err);
          alert(`Failed to open file: ${err.message}`);
        }
        resolve();
      });
      input.click();
    });
  }

  async _loadTMJ(tmj, fileName) {
    const parsed = parseTMJ(tmj);

    // Load tileset images — try IDB library first, then URL, then prompt
    const tilesetImages = new Map();
    const cloudStorage = window.__tileforge?.cloudStorage;
    for (const ts of parsed.tilesets) {
      if (ts.image) {
        let img = null;

        // 1. Try loading from tileset library (IDB)
        if (cloudStorage) {
          // Direct IDB path match: "tilesets/xxx/image.png"
          if (ts.image.startsWith('tilesets/')) {
            try { img = await cloudStorage._loadImageFromIDB(ts.image); } catch { /* fall through */ }
          }
          // Normalized filename match: "Tiny Town (Kenney).png" → search library by name
          if (!img) {
            const tilesetLib = window.__tileforge?.tilesetLibrary;
            if (tilesetLib) {
              const nameFromFile = ts.image.replace(/\.png$/i, '');
              const match = tilesetLib.list().find(m => m.name === nameFromFile || m.name === ts.name);
              if (match) {
                try { img = await cloudStorage._loadImageFromIDB(`tilesets/${match.id}/image.png`); } catch { /* fall through */ }
              }
            }
          }
        }

        // 2. Try loading as URL (for relative paths or absolute URLs)
        if (!img) {
          try {
            img = await this._loadImage(ts.image);
          } catch { /* fall through */ }
        }

        // 3. Prompt user to locate the file
        if (!img) {
          console.warn(`Tileset image not found: ${ts.image}`);
          img = await this._promptForImage(ts.name, ts.image);
        }

        if (img) tilesetImages.set(ts.image, img);
      }
    }

    this.state.batch({
      ...parsed,
      tilesetImages,
      tileAnimations: parsed.tileAnimations || new Map(),
      tileCollisions: parsed.tileCollisions || new Map(),
      fileName,
      dirty: false,
      scrollX: 0,
      scrollY: 0,
      zoom: 1,
      activeLayerIndex: 0,
    });

    this.mapCanvas.invalidateAll();

    // Track in recent files
    this.recentFiles.add(fileName, {
      width: parsed.mapWidth,
      height: parsed.mapHeight,
      layerCount: (parsed.layers || []).length,
      tilesets: parsed.tilesets || [],
    });
  }

  _loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
      img.src = src;
    });
  }

  async _promptForImage(tilesetName, originalPath) {
    const msg = `Tileset "${tilesetName}" image not found at "${originalPath}".\nWould you like to locate it?`;
    if (!confirm(msg)) return null;

    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.addEventListener('change', () => {
        const file = input.files[0];
        if (!file) { resolve(null); return; }
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = URL.createObjectURL(file);
      });
      input.click();
    });
  }
}
